// src/components/configuration/sections/communications/CommunicationsEditor.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Mail,
  MessageSquare,
  Search,
  Plus,
  Circle,
  Play,
  Trash2,
  Save,
  Check,
  X,
  Pencil
} from 'lucide-react';

// shadcn components
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle
} from '../../../ui/resizable';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Separator } from '../../../ui/separator';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Spinner } from '../../../ui/spinner';
import { ScrollArea } from '../../../ui/scroll-area';
import { Switch } from '../../../ui/switch';
import { Label } from '../../../ui/label';
import { Card } from '../../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';

// Context and utilities
import { useSettings } from '../../../../hooks/useSettings';
import { cn } from '../../../../utils/cn';
import PageContainer from '../../../ui/layout/PageContainer/PageContainer';
import EmailTemplateEditor from './EmailTemplateEditor';
import InspectorContent from '../../../email-builder/App/InspectorDrawer/InspectorContent.jsx';
import { getVariableLabelFromKey } from '../../../../constants/templateVariables';
import { convertVariablesToPlaceholderText } from '../../../../constants/variablePlaceholders';
import { useVariableCompletion } from '../../../../hooks/useVariableCompletion';
import { VariableCommandMenu } from '../../../ui/variable-command-menu';
import { Kbd } from '../../../ui/kbd';
import { TemplateMetadataProvider } from './TemplateMetadataContext';
import { LocalSidebarRail } from '../../../ui/local-sidebar-rail';

// Utility: Convert {{brackets}} to pills
const convertBracketsToPills = (html) => {
  if (!html) return html;
  return html.replace(/\{\{([^}]+)\}\}/g, (match, variableKey) => {
    const variableLabel = getVariableLabelFromKey(variableKey.trim());
    return `<span class="variable-pill" draggable="true" data-variable="${variableKey.trim()}" contenteditable="false">${variableLabel}<button class="pill-delete" contenteditable="false" type="button">&times;</button></span>`;
  });
};

// Utility: Convert pills to {{brackets}}
const convertPillsToBrackets = (html) => {
  if (!html) return html;
  const result = html.replace(/<span[^>]*class="variable-pill"[^>]*data-variable="([^"]+)"[^>]*>.*?<\/span>/gs, '{{$1}}');
  return result;
};

// Helper: Render text with placeholders styled in blue
const renderTextWithPlaceholders = (text) => {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  const regex = /\{\{([^}]+)\}\}/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the variable
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }

    // Add the placeholder
    const variableKey = match[1].trim();
    const placeholder = convertVariablesToPlaceholderText(`{{${variableKey}}}`);
    parts.push({
      type: 'placeholder',
      content: placeholder,
      key: variableKey
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }

  return parts.map((part, idx) => {
    if (part.type === 'placeholder') {
      return (
        <span
          key={idx}
          className="text-blue-400 font-medium"
          title={`Variable: {{${part.key}}}`}
        >
          {part.content}
        </span>
      );
    }
    return <React.Fragment key={idx}>{part.content}</React.Fragment>;
  });
};

// Utility: HTML to plain text
const htmlToText = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  tempDiv.innerHTML = tempDiv.innerHTML.replace(/<br\s*\/?>/gi, '\n');
  tempDiv.innerHTML = tempDiv.innerHTML.replace(/<\/p>/gi, '\n\n');
  let text = tempDiv.textContent || tempDiv.innerText || '';
  text = text.replace(/\n\s*\n\s*\n/g, '\n\n');
  text = text.trim();
  return text;
};

const CommunicationsEditor = () => {
  // Settings hook
  const {
    loading: settingsLoading,
    error,
    isConnected,
    emailTemplates,
    smsTemplates,
    savingTemplates,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    createSMSTemplate,
    updateSMSTemplate,
    deleteSMSTemplate,
    testTemplate,
    getEmailTemplateById,
    getSMSTemplateById
  } = useSettings();

  // UI State
  const [templateType, setTemplateType] = useState('email'); // 'email' or 'sms'
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [emailEditorHasChanges, setEmailEditorHasChanges] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);

  // Inspector panel size persistence (email only)
  const [inspectorSize, setInspectorSize] = useState(() => {
    const saved = localStorage.getItem('communications-inspector-size');
    return saved ? parseFloat(saved) : 20;
  });

  // Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateType, setNewTemplateType] = useState('email');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body_html: '',
    message_body: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  // Refs for variable insertion (SMS only) and email editor
  const smsEditorRef = useRef(null);
  const draggedPillRef = useRef(null);
  const emailEditorRef = useRef(null);

  // Canonical pattern refs
  const prevSavingTemplates = useRef(false);
  const prevTemplateId = useRef(null);

  // Variable completion for SMS editor
  const smsVariableCompletion = useVariableCompletion({
    editorRef: smsEditorRef,
    onInsert: (variableKey) => {
      if (smsEditorRef.current) {
        insertPillIntoContentEditable(smsEditorRef.current, variableKey);
      }
    },
    reinitKey: selectedTemplateId, // Re-initialize when template changes to ensure listeners attach
  });

  // Get templates based on type
  const allTemplates = useMemo(() => {
    return templateType === 'email' ? emailTemplates : smsTemplates;
  }, [emailTemplates, smsTemplates, templateType]);

  // Get selected template
  const selectedTemplate = selectedTemplateId ?
    (templateType === 'email' ?
      getEmailTemplateById(selectedTemplateId) :
      getSMSTemplateById(selectedTemplateId)
    ) : null;

  // Filter templates
  const filteredTemplates = allTemplates.filter(template =>
    !searchTerm ||
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Auto-select first template
  useEffect(() => {
    if (allTemplates && allTemplates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(allTemplates[0].id);
    }
  }, [templateType, allTemplates, selectedTemplateId]);

  // Canonical: Initialize form on template switch, guard on same-template server updates
  useEffect(() => {
    if (!selectedTemplate) return;

    const isTemplateSwitch = prevTemplateId.current !== selectedTemplate.id;
    prevTemplateId.current = selectedTemplate.id;

    if (isTemplateSwitch) {
      // Always reset on template switch
      setFormData({
        name: selectedTemplate.name || '',
        subject: selectedTemplate.subject || '',
        body_html: selectedTemplate.body_html || '',
        message_body: selectedTemplate.message_body || ''
      });
      setHasChanges(false);
      setFormErrors({});
      setIsConfirmingDelete(false);

      if (smsEditorRef.current && templateType === 'sms') {
        smsEditorRef.current.innerHTML = convertBracketsToPills(selectedTemplate.message_body || '').replace(/\n/g, '<br>');
      }
    } else if (!hasChanges && !emailEditorHasChanges) {
      // Same template, server update — only sync if no in-progress edits
      setFormData({
        name: selectedTemplate.name || '',
        subject: selectedTemplate.subject || '',
        body_html: selectedTemplate.body_html || '',
        message_body: selectedTemplate.message_body || ''
      });

      if (smsEditorRef.current && templateType === 'sms') {
        smsEditorRef.current.innerHTML = convertBracketsToPills(selectedTemplate.message_body || '').replace(/\n/g, '<br>');
      }
    }
    // If same template and user has edits, do nothing — preserve in-progress work
  }, [selectedTemplate]);

  // Track changes
  useEffect(() => {
    if (selectedTemplate) {
      const changed =
        formData.name !== (selectedTemplate.name || '') ||
        formData.subject !== (selectedTemplate.subject || '') ||
        formData.body_html !== (selectedTemplate.body_html || '') ||
        formData.message_body !== (selectedTemplate.message_body || '');
      setHasChanges(changed);
    }
  }, [formData, selectedTemplate]);

  // Canonical: Saving flag watcher — reset on server confirmation (true→false)
  useEffect(() => {
    if (prevSavingTemplates.current && !savingTemplates) {
      // Create modal — close on confirmation
      if (isCreateModalOpen) {
        setIsCreateModalOpen(false);
        setNewTemplateName('');
        setFormErrors({});
        if (newTemplateType !== templateType) {
          setTemplateType(newTemplateType);
        }
      }

      // Inline rename — clear editing state
      if (editingTemplateId) {
        setEditingTemplateId(null);
        setEditingName('');
      }

      // Delete — if selected template no longer exists, clear selection
      if (selectedTemplateId) {
        const templates = templateType === 'email' ? emailTemplates : smsTemplates;
        if (!templates.some(t => t.id === selectedTemplateId)) {
          setSelectedTemplateId(null);
          setIsConfirmingDelete(false);
          prevSavingTemplates.current = savingTemplates;
          return;
        }
      }

      // Reset form from store truth
      if (selectedTemplate && templateType === 'sms') {
        setFormData({
          name: selectedTemplate.name || '',
          subject: selectedTemplate.subject || '',
          body_html: selectedTemplate.body_html || '',
          message_body: selectedTemplate.message_body || ''
        });
        if (smsEditorRef.current) {
          smsEditorRef.current.innerHTML = convertBracketsToPills(selectedTemplate.message_body || '').replace(/\n/g, '<br>');
        }
      }

      if (selectedTemplate && templateType === 'email' && emailEditorRef.current) {
        emailEditorRef.current.resetChanges();
      }

      setIsConfirmingDelete(false);
    }
    prevSavingTemplates.current = savingTemplates;
  }, [savingTemplates, selectedTemplate, templateType, isCreateModalOpen, newTemplateType, editingTemplateId, selectedTemplateId, emailTemplates, smsTemplates]);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    setNewTemplateName('');
    setNewTemplateType(templateType);
    setIsCreateModalOpen(true);
  };

  // Handle actual template creation from modal — fire-and-forget
  const handleCreateTemplate = () => {
    if (!newTemplateName.trim()) {
      setFormErrors({ create: 'Template name is required' });
      return;
    }

    const templateData = {
      name: newTemplateName.trim(),
      active: false,
      ...(newTemplateType === 'email' ? {
        subject: '',
        body_html: '',
        body_text: '',
        builder_config: {
          root: {
            type: 'EmailLayout',
            data: {
              backdropColor: '#1a1a1a',
              canvasColor: '#FFFFFF',
              textColor: '#242424',
              fontFamily: 'MODERN_SANS',
              childrenIds: []
            }
          }
        },
        template_type: 'builder'
      } : {
        message_body: ''
      })
    };

    if (newTemplateType === 'email') {
      createEmailTemplate(templateData);
    } else {
      createSMSTemplate(templateData);
    }
    // Modal close + type switch handled by saving flag watcher
  };

  // Save template — fire-and-forget
  const handleSave = () => {
    // Email templates - get data from child editor
    if (templateType === 'email' && emailEditorRef.current) {
      const templateData = emailEditorRef.current.getSaveData();
      updateEmailTemplate(selectedTemplate.id, templateData);
      // resetChanges() handled by saving flag watcher
      return;
    }

    // SMS templates — validate then emit
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (templateType === 'sms') {
      if (!formData.message_body.trim()) errors.message_body = 'Message is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const data = {
      name: formData.name.trim(),
      active: selectedTemplate?.active || false,
      message_body: formData.message_body
    };

    updateSMSTemplate(selectedTemplate.id, data);
    // Form reset handled by saving flag watcher
  };

  // Delete template — fire-and-forget
  const handleDelete = () => {
    if (templateType === 'email') {
      deleteEmailTemplate(selectedTemplate.id);
    } else {
      deleteSMSTemplate(selectedTemplate.id);
    }
    // Selection clear handled by saving flag watcher
  };

  // Test template — fire-and-forget
  const handleTest = () => {
    testTemplate(selectedTemplate.id, templateType, {
      customer_name: 'John Doe',
      service_type: 'Pool Inspection',
      appointment_date: 'Tomorrow',
      appointment_time: '10:00 AM'
    });
  };

  // Toggle active — fire-and-forget
  const handleToggleActive = () => {
    // Validate: cannot activate email template without subject
    if (!selectedTemplate.active && templateType === 'email') {
      if (!selectedTemplate.subject?.trim()) {
        setFormErrors({ save: 'Subject line is required to activate email template' });
        return;
      }
    }

    const data = {
      name: selectedTemplate.name,
      active: !selectedTemplate.active,
      ...(templateType === 'email' ? {
        subject: selectedTemplate.subject,
        body_html: selectedTemplate.body_html,
        body_text: selectedTemplate.body_text
      } : {
        message_body: selectedTemplate.message_body
      })
    };
    if (templateType === 'email') {
      updateEmailTemplate(selectedTemplate.id, data);
    } else {
      updateSMSTemplate(selectedTemplate.id, data);
    }
  };

  // Inline editing handlers
  const handleStartEdit = (template, e) => {
    e.stopPropagation();
    setEditingTemplateId(template.id);
    setEditingName(template.name);
  };

  const handleCancelEdit = (e) => {
    e?.stopPropagation();
    setEditingTemplateId(null);
    setEditingName('');
  };

  const handleSaveEdit = (template, e) => {
    e?.stopPropagation();

    if (!editingName.trim()) {
      handleCancelEdit();
      return;
    }

    const data = {
      name: editingName.trim(),
      active: template.active,
      ...(templateType === 'email' ? {
        subject: template.subject,
        body_html: template.body_html,
        body_text: template.body_text,
        builder_config: template.builder_config,
        template_type: template.template_type
      } : {
        message_body: template.message_body
      })
    };

    if (templateType === 'email') {
      updateEmailTemplate(template.id, data);
    } else {
      updateSMSTemplate(template.id, data);
    }
    // Editing state clear handled by saving flag watcher
  };

  // Drag and drop handlers for pills
  const handleDragStart = (e) => {
    const pill = e.target.closest('.variable-pill');
    if (pill) {
      draggedPillRef.current = pill;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', pill.outerHTML);

      // Create a semi-transparent ghost image
      const ghostPill = pill.cloneNode(true);
      ghostPill.style.opacity = '0.4';
      ghostPill.style.position = 'absolute';
      ghostPill.style.top = '-1000px';
      document.body.appendChild(ghostPill);
      e.dataTransfer.setDragImage(ghostPill, 0, 0);

      // Remove the ghost after a brief delay
      setTimeout(() => ghostPill.remove(), 0);
    }
  };

  const handleDragEnd = (e) => {
    // Clean up ref if drag was cancelled
    if (draggedPillRef.current) {
      draggedPillRef.current = null;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Show caret at drop position
    const range = document.caretRangeFromPoint(e.clientX, e.clientY);
    if (range) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const handleDrop = (e, editorElement) => {
    e.preventDefault();

    if (!draggedPillRef.current) return;

    // Get the drop position
    const range = document.caretRangeFromPoint(e.clientX, e.clientY);
    if (!range) return;

    // Remove the original pill
    const originalPill = draggedPillRef.current;
    originalPill.remove();

    // Insert at new position
    const pillClone = originalPill.cloneNode(true);
    range.insertNode(pillClone);

    // Clean up
    draggedPillRef.current = null;

    // Trigger change event (SMS only)
    const html = editorElement.innerHTML;
    const converted = convertPillsToBrackets(html.replace(/<br>/g, '\n').replace(/<div>/g, '\n').replace(/<\/div>/g, ''));

    if (editorElement === smsEditorRef.current) {
      handleFieldChange('message_body', converted);
    }
  };

  // Insert variable pill into contenteditable element
  const insertPillIntoContentEditable = (element, variableKey) => {
    const label = getVariableLabelFromKey(variableKey);
    const pillHTML = `<span class="variable-pill" draggable="true" data-variable="${variableKey}" contenteditable="false">${label}<button class="pill-delete" contenteditable="false" type="button">&times;</button></span>`;

    // Check if adding this variable would exceed the limit
    const currentHtml = element.innerHTML;
    const currentConverted = convertPillsToBrackets(currentHtml.replace(/<br>/g, '\n').replace(/<div>/g, '\n').replace(/<\/div>/g, ''));
    const testConverted = currentConverted + `{{${variableKey}}}`;
    const testPreview = convertVariablesToPlaceholderText(testConverted);

    if (testPreview.length > 480) {
      // Don't insert - would exceed limit
      return;
    }

    element.focus();
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = pillHTML;
      const frag = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        frag.appendChild(tempDiv.firstChild);
      }
      range.insertNode(frag);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      element.innerHTML += pillHTML;
    }

    // Trigger change event
    const event = new Event('input', { bubbles: true });
    element.dispatchEvent(event);
  };

  // Handle layout change to persist inspector panel size
  const handleLayoutChange = (sizes) => {
    if (templateType === 'email' && sizes.length === 2) {
      // sizes[1] is the right panel (inspector) for email templates
      const newInspectorSize = sizes[1];
      setInspectorSize(newInspectorSize);
      localStorage.setItem('communications-inspector-size', newInspectorSize.toString());
    }
  };

  // Template metadata for context provider
  const templateMetadata = useMemo(() => ({
    subject: formData.subject,
    onSubjectChange: (value) => handleFieldChange('subject', value)
  }), [formData.subject]);

  // Loading states
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>
            Unable to connect to settings service. Please check your connection.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  if (settingsLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading templates...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      <div className="flex h-full relative">
        {/* LEFT PANEL - Template List (shadcn-inspired architecture) */}
        {/* Outer Spacer: Controls layout width, no overflow/border/bg */}
        <div
          className={cn(
            "relative flex-shrink-0 transition-all duration-300 ease-in-out",
            leftPanelCollapsed ? "w-0" : "w-[320px]"
          )}
        >
          {/* Inner Container: Always 320px, slides in/out */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 w-[320px] border-r border-border bg-slate-900/50 flex flex-col transition-transform duration-300 ease-in-out",
              leftPanelCollapsed && "-translate-x-full"
            )}
          >
            {/* Hover Rail - Edge collapse/expand control */}
            <LocalSidebarRail
              collapsed={leftPanelCollapsed}
              onToggle={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              side="left"
            />

            {/* Header - Administrative Layer */}
            <div className="p-3 border-b flex-shrink-0" data-layer="administrative">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold">Templates</h2>
              <Button
                size="sm"
                onClick={handleOpenCreateModal}
                className="layer-button font-medium"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New
              </Button>
            </div>

            {/* Search */}
            <div className="mb-3">
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-slate-400 transition-colors duration-300 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="layer-input pl-10"
                />
              </div>
            </div>

              {/* Type Toggle */}
              <Tabs value={templateType} onValueChange={setTemplateType}>
                <TabsList className="w-full h-9 p-0 bg-transparent">
                  <TabsTrigger
                    value="email"
                    className="flex-1 h-9 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email ({emailTemplates.length})
                  </TabsTrigger>
                  <TabsTrigger
                    value="sms"
                    className="flex-1 h-9 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    SMS ({smsTemplates.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Template List */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-2">
                {filteredTemplates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No templates found
                  </div>
                ) : (
                  filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => editingTemplateId !== template.id && setSelectedTemplateId(template.id)}
                      className={cn(
                        "w-full text-left px-3 py-2.5 rounded-md transition-all",
                        "flex items-start justify-between gap-2",
                        "border bg-card cursor-pointer",
                        "hover:bg-accent hover:shadow-[0_2px_8px_rgba(0,0,0,0.4)] hover:border-border hover:scale-[1.02]",
                        selectedTemplateId === template.id && "bg-accent shadow-[0_2px_8px_rgba(0,0,0,0.4)] border-primary scale-[1.02]",
                        !selectedTemplateId === template.id && "border-transparent shadow-[0_1px_2px_rgba(0,0,0,0.2)]",
                        "group"
                      )}
                    >
                      {editingTemplateId === template.id ? (
                        <>
                          {/* Editing mode */}
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(template, e);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit(e);
                              }
                            }}
                            className="h-7 text-sm flex-1"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={(e) => handleSaveEdit(template, e)}
                          >
                            <Check className="h-3.5 w-3.5 text-emerald-500" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={handleCancelEdit}
                          >
                            <X className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Normal mode */}
                          <div className="min-w-0 flex-1 text-left">
                            <p className="font-medium text-sm truncate">
                              {template.name}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => handleStartEdit(template, e)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Circle
                            className={cn(
                              "w-2 h-2 flex-shrink-0 mt-1.5",
                              template.active
                                ? "fill-emerald-500 text-emerald-500"
                                : "fill-muted-foreground/50 text-muted-foreground/50"
                            )}
                          />
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Template Actions */}
            {selectedTemplate && (
              <>
                <Separator />
                <div className="p-3 space-y-3 flex-shrink-0">
                  {/* Active/Inactive Switch */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-card border">
                    <Label
                      htmlFor="template-active-switch"
                      className={cn(
                        "text-sm font-medium cursor-pointer",
                        selectedTemplate.active ? "text-emerald-400" : "text-muted-foreground"
                      )}
                    >
                      {selectedTemplate.active ? 'Active' : 'Inactive'}
                    </Label>
                    <Switch
                      id="template-active-switch"
                      checked={selectedTemplate.active}
                      onCheckedChange={handleToggleActive}
                      disabled={savingTemplates}
                      className={cn(
                        selectedTemplate.active && "data-[state=checked]:bg-emerald-500"
                      )}
                    />
                  </div>

                  {/* Test Send Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={handleTest}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Test Send
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* MAIN CONTENT - Center and Right Panels */}
        <div className="flex-1 flex flex-col min-w-0">
          <TemplateMetadataProvider value={templateMetadata}>
            <ResizablePanelGroup direction="horizontal" className="h-full" onLayout={handleLayoutChange}>
            {/* CENTER PANEL - Editor */}
            <ResizablePanel defaultSize={templateType === 'email' ? 80 - inspectorSize : 100} minSize={40}>
          <div className="h-full flex flex-col bg-background">
            {!selectedTemplate ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-muted-foreground">Select a template to edit</p>
              </div>
            ) : (
              <>
                {/* Editor Area */}
                <div className="flex-1 min-h-0">
                  {templateType === 'email' ? (
                    <div className="h-full">
                      <EmailTemplateEditor
                        ref={emailEditorRef}
                        template={selectedTemplate}
                        name={formData.name}
                        subject={formData.subject}
                        active={selectedTemplate?.active ?? false}
                        onSubjectChange={(value) => handleFieldChange('subject', value)}
                        onHasChangesChange={setEmailEditorHasChanges}
                      />
                    </div>
                  ) : (
                    <div className="h-full bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100% overflow-y-auto">
                      {/* Header - Full Width with Consistent Padding */}
                      <div className="pt-20 pl-36 pr-36 space-y-4">
                        <h1 className="text-4xl font-bold text-foreground">
                          {selectedTemplate.name}
                        </h1>
                        <div className="h-0.5 w-full bg-gradient-to-r from-primary/40 to-transparent"></div>
                      </div>

                      {/* Content - Centered Container */}
                      <div className="pt-10 pb-10 px-20 max-w-5xl mx-auto space-y-8">
                        {/* Description - Variables System */}
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Press <Kbd>/</Kbd> to insert dynamic variables like customer name or appointment details. Variables are replaced with real data when sent.
                        </p>

                        {/* Two-Column Grid - Message and Preview */}
                        <div className="grid grid-cols-2 gap-6">
                          {/* Left Column - Message Editor */}
                          <div className="space-y-2">
                            <div className="relative">
                              <div
                                ref={smsEditorRef}
                                contentEditable
                                suppressContentEditableWarning
                                onInput={(e) => {
                                  const html = e.currentTarget.innerHTML;
                                  const converted = convertPillsToBrackets(html.replace(/<br>/g, '\n').replace(/<div>/g, '\n').replace(/<\/div>/g, ''));

                                  // Check character limit (3 SMS segments = 480 chars)
                                  const previewText = convertVariablesToPlaceholderText(converted);
                                  if (previewText.length > 480) {
                                    // Prevent input - restore previous content
                                    e.currentTarget.innerHTML = convertBracketsToPills(formData.message_body || '').replace(/\n/g, '<br>');
                                    // Move cursor to end
                                    const range = document.createRange();
                                    const sel = window.getSelection();
                                    range.selectNodeContents(e.currentTarget);
                                    range.collapse(false);
                                    sel.removeAllRanges();
                                    sel.addRange(range);
                                    return;
                                  }

                                  handleFieldChange('message_body', converted);
                                }}
                                onClick={(e) => {
                                  if (e.target.classList.contains('pill-delete')) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const pill = e.target.closest('.variable-pill');
                                    if (pill) {
                                      pill.remove();
                                      const html = smsEditorRef.current.innerHTML;
                                      const converted = convertPillsToBrackets(html.replace(/<br>/g, '\n').replace(/<div>/g, '\n').replace(/<\/div>/g, ''));
                                      handleFieldChange('message_body', converted);
                                    }
                                  }
                                }}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, smsEditorRef.current)}
                                onDragEnd={handleDragEnd}
                                className={cn(
                                  "min-h-[280px] p-4 pb-8 rounded-lg border bg-zinc-900 border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]",
                                  "font-mono text-base leading-relaxed outline-none overflow-y-auto",
                                  "transition-all duration-200",
                                  "focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5",
                                  formErrors.message_body && "border-destructive ring-2 ring-destructive/20",
                                  "empty:before:content-['Type_your_SMS_message...'] empty:before:text-muted-foreground",
                                  "[&_.variable-pill]:inline-flex [&_.variable-pill]:items-center [&_.variable-pill]:gap-1",
                                  "[&_.variable-pill]:bg-blue-500/20 [&_.variable-pill]:text-white",
                                  "[&_.variable-pill]:px-2 [&_.variable-pill]:py-0.5 [&_.variable-pill]:rounded",
                                  "[&_.variable-pill]:border [&_.variable-pill]:border-blue-500/30",
                                  "[&_.variable-pill]:font-mono [&_.variable-pill]:text-sm",
                                  "[&_.variable-pill]:mx-0.5 [&_.variable-pill]:cursor-move",
                                  "[&_.variable-pill]:select-none [&_.variable-pill]:align-middle [&_.variable-pill]:relative",
                                  "[&_.variable-pill:hover]:bg-blue-500/30 [&_.variable-pill:hover]:pr-6",
                                  "[&_.pill-delete]:hidden [&_.pill-delete]:absolute [&_.pill-delete]:right-1",
                                  "[&_.pill-delete]:text-red-400 [&_.pill-delete]:cursor-pointer",
                                  "[&_.pill-delete]:border-0 [&_.pill-delete]:bg-transparent [&_.pill-delete]:text-base",
                                  "[&_.variable-pill:hover_.pill-delete]:inline"
                                )}
                              />
                              {(() => {
                                // Calculate character count using placeholder text (realistic length)
                                const messageWithBrackets = formData.message_body ? convertPillsToBrackets(formData.message_body.replace(/<br>/g, '\n').replace(/<div>/g, '\n').replace(/<\/div>/g, '')) : '';
                                const previewText = convertVariablesToPlaceholderText(messageWithBrackets);
                                const charCount = previewText.length;
                                const maxChars = 480; // 3 SMS segments

                                return (
                                  <div className="absolute bottom-2 left-2 text-xs">
                                    <span className={cn(
                                      "px-2 py-1 rounded bg-black/50 backdrop-blur-sm",
                                      charCount >= maxChars && "text-red-400",
                                      charCount < maxChars && "text-muted-foreground"
                                    )}>
                                      {charCount}/{maxChars}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                            {formErrors.message_body && (
                              <p className="text-sm text-destructive">{formErrors.message_body}</p>
                            )}

                            {/* SMS Action Controls */}
                            <div className="pt-6 space-y-4">
                              <div className="h-px bg-gradient-to-r from-zinc-700/50 to-transparent"></div>
                              <div className="flex items-center justify-between">
                                {!isConfirmingDelete ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsConfirmingDelete(true)}
                                    className="bg-gradient-to-br from-red-900/20 to-red-950/20 border-red-800/30 text-red-400 hover:bg-gradient-to-br hover:from-red-900/30 hover:to-red-950/30 hover:border-red-700/40 shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.4)] transition-all"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </Button>
                                ) : (
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleDelete}
                                      className="bg-gradient-to-br from-red-900/20 to-red-950/20 border-red-800/30 text-red-400 hover:bg-gradient-to-br hover:from-red-900/30 hover:to-red-950/30 hover:border-red-700/40 shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.4)] transition-all"
                                    >
                                      <Check className="mr-2 h-4 w-4" />
                                      Confirm
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setIsConfirmingDelete(false)}
                                      className="bg-gradient-to-br from-zinc-800 to-zinc-900 border-zinc-700 hover:bg-gradient-to-br hover:from-zinc-700 hover:to-zinc-800 shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.4)] transition-all"
                                    >
                                      <X className="mr-2 h-4 w-4" />
                                      Cancel
                                    </Button>
                                  </div>
                                )}

                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-muted-foreground">
                                    {hasChanges ? 'Unsaved changes' : 'All changes saved'}
                                  </span>
                                  <Button
                                    onClick={handleSave}
                                    disabled={!hasChanges}
                                    size="sm"
                                    className="bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 hover:bg-gradient-to-br hover:from-zinc-700 hover:to-zinc-800 hover:-translate-y-0.5 shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:shadow-[0_4px_8px_rgba(0,0,0,0.4)] transition-all disabled:opacity-50 disabled:hover:translate-y-0"
                                  >
                                    <Save className="mr-2 h-4 w-4" />
                                    Save
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Variable completion popover for SMS */}
                          {smsVariableCompletion.isOpen && smsVariableCompletion.position && (
                            <div
                              style={{
                                position: 'fixed',
                                top: smsVariableCompletion.position.top,
                                left: smsVariableCompletion.position.left,
                                zIndex: 9999,
                              }}
                            >
                              <div className="w-80 border border-border shadow-lg rounded-lg overflow-hidden">
                                <VariableCommandMenu
                                  ref={smsVariableCompletion.commandMenuRef}
                                  query={smsVariableCompletion.query}
                                  onSelect={smsVariableCompletion.onSelect}
                                  isEmail={false}
                                />
                              </div>
                            </div>
                          )}

                          {/* Right Column - Device Preview */}
                          <div className="flex items-center justify-center">
                            {/* Phone Container */}
                            <div className="relative w-[340px] h-[620px] bg-zinc-950 rounded-[3rem] border-[4px] border-zinc-800 shadow-[0_0_0_1px_rgba(39,39,42,1),0_20px_60px_rgba(0,0,0,0.5)]">
                              {/* Notch */}
                              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[26px] bg-zinc-950 rounded-b-[1rem] z-10"></div>

                              {/* Screen Content Area */}
                              <div className="relative h-full overflow-hidden rounded-[2.5rem]">
                                {/* Status Bar */}
                                <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/40 to-transparent z-10 flex items-center justify-between px-6 pt-2">
                                  <div className="text-white text-xs font-semibold">9:41</div>
                                  <div className="flex items-center gap-1">
                                    <div className="w-4 h-3 border border-white/60 rounded-sm relative">
                                      <div className="absolute inset-0.5 bg-white/80 rounded-[1px]"></div>
                                    </div>
                                  </div>
                                </div>

                                {/* Messages Screen */}
                                <div className="h-full bg-black pt-16 px-4 overflow-y-auto">
                                  {(() => {
                                    if (!formData.message_body) {
                                      return (
                                        <div className="flex justify-start">
                                          <div className="relative max-w-[85%]">
                                            <div className="bg-slate-700 rounded-[18px] rounded-tl-[4px] px-4 py-2.5 shadow-md">
                                              <p className="text-slate-400 text-[15px] leading-[20px] italic">
                                                Your message will appear here...
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    // Convert pills to brackets for preview
                                    const cleanedHtml = formData.message_body.replace(/<br>/g, '\n').replace(/<div>/g, '\n').replace(/<\/div>/g, '');
                                    const messageWithBrackets = convertPillsToBrackets(cleanedHtml);

                                    return (
                                      <div className="flex justify-start">
                                        <div className="relative max-w-[85%]">
                                          <div className="bg-slate-700 rounded-[18px] rounded-tl-[4px] px-4 py-2.5 shadow-md">
                                            <p className="text-white text-[15px] leading-[20px] whitespace-pre-wrap break-words">
                                              {renderTextWithPlaceholders(messageWithBrackets)}
                                            </p>
                                          </div>
                                          <p className="text-[11px] text-slate-500 mt-1 ml-1">Just now</p>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Actions - Email Only */}
                {templateType === 'email' && (
                  <>
                    <Separator />
                    <div className="flex-shrink-0 p-4 flex items-center justify-between">
                  {!isConfirmingDelete ? (
                    <Button variant="destructive" size="sm" onClick={() => setIsConfirmingDelete(true)}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="destructive" size="sm" onClick={handleDelete}>
                        <Check className="mr-2 h-4 w-4" />
                        Confirm
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setIsConfirmingDelete(false)}>
                        <X className="mr-2 h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      {emailEditorHasChanges ? 'Unsaved changes' : 'All changes saved'}
                    </span>
                    <Button onClick={handleSave} disabled={!emailEditorHasChanges}>
                      <Save className="mr-2 h-4 w-4" />
                      Save
                    </Button>
                  </div>
                </div>
                  </>
                )}
              </>
            )}
          </div>
        </ResizablePanel>

            {/* RIGHT PANEL - Inspector (Email only, conditionally rendered) */}
            {selectedTemplate && templateType === 'email' && (
              <>
                <ResizableHandle withHandle />

                <ResizablePanel
                  defaultSize={inspectorSize}
                  minSize={22}
                  maxSize={40}
                >
                  <InspectorContent />
                </ResizablePanel>
              </>
            )}
            </ResizablePanelGroup>
          </TemplateMetadataProvider>
        </div>
      </div>

      {/* Create Template Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={(open) => { if (!savingTemplates) setIsCreateModalOpen(open); }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
            <DialogDescription>
              Create a new email or SMS template. Templates are created as inactive.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-3">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Enter template name..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateTemplate();
                  }
                }}
              />
              {formErrors.create && (
                <p className="text-sm text-destructive">{formErrors.create}</p>
              )}
            </div>
            <div className="grid gap-3">
              <Label>Template Type</Label>
              <RadioGroup value={newTemplateType} onValueChange={setNewTemplateType}>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="email" id="type-email" />
                  <Label htmlFor="type-email" className="font-normal cursor-pointer">
                    Email Template
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="sms" id="type-sms" />
                  <Label htmlFor="type-sms" className="font-normal cursor-pointer">
                    SMS Template
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate}>
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default CommunicationsEditor;
